import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Bilingue, Devise, EtatExposition, TypeExposition } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import type { Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee, precedentDansLaPortee } from './cles.js';

/**
 * Expositions, foires et événements — `/v1/g/:slug/expositions`
 * (`docs/dev/api.md` § 15). Les formes suivent
 * `apps/api/src/expositions/expositions.routes.ts` au champ près : un seul
 * type ici, jamais un par écran (même règle qu'`api/oeuvres.ts`).
 *
 * L'ÉTAT (`brouillon`, `annoncee`, `en_cours`, `passee`) est posé à la main,
 * jamais déduit de l'horloge (§ 15.2, D13) : aucune fonction de ce module ne
 * calcule un état depuis les dates.
 */
export interface Vernissage {
  readonly date: string;
  readonly debut: string;
  readonly fin: string | null;
  readonly note: Bilingue | null;
}

export interface ExpositionPresentee {
  readonly id: string;
  readonly type: TypeExposition;
  readonly titre: Bilingue;
  readonly sousTitre: Bilingue | null;
  readonly slug: string;
  readonly slugPublie: string | null;
  readonly debutLe: string;
  readonly finLe: string;
  readonly vernissage: Vernissage | null;
  readonly lieuId: string | null;
  readonly stand: string | null;
  readonly texte: Bilingue | null;
  readonly artistes: readonly string[];
  readonly oeuvres: readonly string[];
  readonly etat: EtatExposition;
  readonly visibleSurLeSite: boolean;
  readonly archive: boolean;
  readonly creeLe: string;
  readonly modifieLe: string;
}

export interface ArtisteDeLExposition {
  readonly id: string;
  readonly nom: string;
  readonly slug: string;
}

export interface OeuvreDeLExposition {
  readonly id: string;
  readonly titre: string;
  readonly artisteNom: string;
  readonly numeroInventaire: string;
  readonly statut: string;
  readonly mediaPrincipalId: string | null;
}

export interface VueAccrochage {
  readonly id: string;
  readonly cleOriginale: string;
  readonly variantes: readonly { cle: string; largeur: number; hauteur: number; format: string }[];
  readonly type: string;
  readonly legende: Bilingue | null;
  readonly credit: string | null;
  readonly ordre: number;
}

export interface FicheExposition {
  readonly exposition: ExpositionPresentee;
  readonly lieu: { readonly id: string; readonly nom: string; readonly type: string } | null;
  readonly artistes: readonly ArtisteDeLExposition[];
  readonly oeuvres: readonly OeuvreDeLExposition[];
  readonly vuesAccrochage: readonly VueAccrochage[];
}

export interface CorpsExposition {
  type?: TypeExposition;
  titre: { fr: string; en?: string };
  sousTitre?: { fr: string; en?: string };
  slug?: string;
  debutLe: string;
  finLe: string;
  vernissage?: {
    date: string;
    debut: string;
    fin?: string;
    note?: { fr: string; en?: string };
  } | null;
  lieuId?: string | null;
  stand?: string | null;
  texte?: { fr: string; en?: string };
  artistes?: readonly string[];
  oeuvres?: readonly string[];
  etat?: EtatExposition;
  visibleSurLeSite?: boolean;
}

export type CorpsMiseAJourExposition = Partial<CorpsExposition>;

export interface FiltresExpositions {
  q?: string;
  type?: TypeExposition;
  etat?: readonly EtatExposition[];
  annee?: number;
  visibleSurSite?: boolean;
  inclureArchivees?: boolean;
  tri?: 'debut_recent' | 'debut_ancien' | 'titre' | 'recent';
  page?: number;
  parPage?: number;
}

export interface ReponseListeExpositions {
  expositions: readonly ExpositionPresentee[];
  pagination: Pagination;
}

export interface EntreeListeDePrix {
  readonly oeuvreId: string;
  readonly oeuvre: {
    readonly artiste: string;
    readonly titre: string;
    readonly annee?: string;
    readonly technique?: string;
    readonly dimensions?: string;
    readonly numeroInventaire?: string;
    readonly edition?: string;
  };
  readonly prixHtCentimes?: number;
  readonly statut: string;
}

export interface ReponseListeDePrix {
  readonly exposition: {
    readonly id: string;
    readonly type: TypeExposition;
    readonly titre: Bilingue;
    readonly sousTitre: Bilingue | null;
    readonly stand: string | null;
    readonly lieu: { readonly id: string; readonly nom: string } | null;
    readonly debutLe: string;
    readonly finLe: string;
  };
  readonly listeDePrix: {
    readonly titre: Bilingue;
    readonly sousTitre?: Bilingue;
    readonly date: string;
    readonly devise: Devise;
    readonly entrees: readonly EntreeListeDePrix[];
  };
  readonly prixMasques: number;
  readonly prixAutreDevise: number;
}

function requeteFiltres(filtres: FiltresExpositions): string {
  const params = new URLSearchParams();
  if (filtres.q) params.set('q', filtres.q);
  if (filtres.type) params.set('type', filtres.type);
  if (filtres.etat && filtres.etat.length > 0) params.set('etat', filtres.etat.join(','));
  if (filtres.annee !== undefined) params.set('annee', String(filtres.annee));
  if (filtres.visibleSurSite !== undefined) {
    params.set('visibleSurSite', String(filtres.visibleSurSite));
  }
  if (filtres.inclureArchivees) params.set('inclureArchivees', 'true');
  params.set('tri', filtres.tri ?? 'debut_recent');
  params.set('page', String(filtres.page ?? 1));
  params.set('parPage', String(filtres.parPage ?? 100));
  return params.toString();
}

export const expositionsApi = {
  lister: (galerie: string, filtres: FiltresExpositions = {}, appel?: AppelApi) =>
    api.get<ReponseListeExpositions>(
      `/g/${encodeURIComponent(galerie)}/expositions?${requeteFiltres(filtres)}`,
      { galerie, ...appel },
    ),
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<FicheExposition>(`/g/${encodeURIComponent(galerie)}/expositions/${id}`, {
      galerie,
      ...appel,
    }),
  creer: (galerie: string, corps: CorpsExposition, appel?: AppelApi) =>
    api.post<{ exposition: ExpositionPresentee }>(
      `/g/${encodeURIComponent(galerie)}/expositions`,
      corps,
      { galerie, ...appel },
    ),
  modifier: (galerie: string, id: string, corps: CorpsMiseAJourExposition, appel?: AppelApi) =>
    api.patch<{ exposition: ExpositionPresentee }>(
      `/g/${encodeURIComponent(galerie)}/expositions/${id}`,
      corps,
      { galerie, ...appel },
    ),
  ordonnerOeuvres: (galerie: string, id: string, oeuvres: readonly string[], appel?: AppelApi) =>
    api.put<{ oeuvres: readonly string[] }>(
      `/g/${encodeURIComponent(galerie)}/expositions/${id}/oeuvres`,
      { oeuvres },
      { galerie, ...appel },
    ),
  archiver: (galerie: string, id: string, raison?: string, appel?: AppelApi) =>
    api.post<{ exposition: ExpositionPresentee }>(
      `/g/${encodeURIComponent(galerie)}/expositions/${id}/archiver`,
      raison ? { raison } : {},
      { galerie, ...appel },
    ),
  restaurer: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<{ exposition: ExpositionPresentee }>(
      `/g/${encodeURIComponent(galerie)}/expositions/${id}/restaurer`,
      {},
      { galerie, ...appel },
    ),
  listeDePrix: (galerie: string, id: string, langue: 'fr' | 'en' = 'fr', appel?: AppelApi) =>
    api.get<ReponseListeDePrix>(
      `/g/${encodeURIComponent(galerie)}/expositions/${id}/liste-de-prix?langue=${langue}`,
      { galerie, ...appel },
    ),
};

const cleListe = (galerie: string, filtres: FiltresExpositions) =>
  cleMetier(galerie, 'expositions', 'liste', filtres);
const cleFiche = (galerie: string, id: string) => cleMetier(galerie, 'expositions', 'fiche', id);

export function useExpositions(
  galerie: string,
  filtres: FiltresExpositions = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: cleListe(galerie, filtres),
    queryFn: (contexte) => expositionsApi.lister(galerie, filtres, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
    enabled: options.enabled ?? true,
  });
}

export function useExposition(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleFiche(galerie, id ?? ''),
    queryFn: (contexte) => expositionsApi.obtenir(galerie, id ?? '', appelDeRequete(contexte)),
    enabled: id !== null,
  });
}

function invaliderExpositions(client: ReturnType<typeof useQueryClient>, galerie: string): void {
  void client.invalidateQueries({ queryKey: cleMetier(galerie, 'expositions', 'liste') });
}

export function useCreerExposition(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsExposition, appel) => expositionsApi.creer(galerie, corps, appel),
      onSuccess: () => invaliderExpositions(client, galerie),
    }),
  );
}

export function useModifierExposition(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: CorpsMiseAJourExposition }, appel) =>
        expositionsApi.modifier(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderExpositions(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

/**
 * Changement d'état (§ 15.2) : geste explicite, confirmé dans un dialogue
 * (l'écran ne l'exécute jamais depuis un simple menu). Optimisme comme pour
 * le statut d'une œuvre : la pastille change avant la réponse du serveur.
 */
export function useChangerEtatExposition(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, etat }: { id: string; etat: EtatExposition }, appel) =>
        expositionsApi.modifier(galerie, id, { etat }, appel),
      onMutate: async ({ id, etat }) => {
        const cle = cleFiche(galerie, id);
        await client.cancelQueries({ queryKey: cle });
        const precedent = client.getQueryData<FicheExposition>(cle);
        if (precedent) {
          client.setQueryData(cle, {
            ...precedent,
            exposition: { ...precedent.exposition, etat },
          });
        }
        return { precedent };
      },
      onError: (_erreur, variables, contexte) => {
        if (contexte?.precedent)
          client.setQueryData(cleFiche(galerie, variables.id), contexte.precedent);
      },
      onSettled: (_donnees, _erreur, variables) => {
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
        invaliderExpositions(client, galerie);
      },
    }),
  );
}

export function useOrdonnerOeuvresExposition(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, oeuvres }: { id: string; oeuvres: readonly string[] }, appel) =>
        expositionsApi.ordonnerOeuvres(galerie, id, oeuvres, appel),
      onSuccess: (_donnees, variables) => {
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useArchiverExposition(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, raison }: { id: string; raison?: string }, appel) =>
        expositionsApi.archiver(galerie, id, raison, appel),
      onSuccess: (_donnees, variables) => {
        invaliderExpositions(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useRestaurerExposition(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => expositionsApi.restaurer(galerie, id, appel),
      onSuccess: (_donnees, id) => {
        invaliderExpositions(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, id) });
      },
    }),
  );
}

export function useListeDePrixExposition(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleMetier(galerie, 'expositions', 'liste-de-prix', id ?? ''),
    queryFn: (contexte) =>
      expositionsApi.listeDePrix(galerie, id ?? '', undefined, appelDeRequete(contexte)),
    enabled: false,
  });
}

export const cleListeExpositions = cleListe;
